// src/pages/founder/ReportsPage.jsx
// Report Generation page - Generate and export pilot metrics reports

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Download,
  Calendar,
  ChevronLeft,
  FileJson,
  Table,
  FileCode,
  Loader2,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { generatePilotReport } from "../../api";

export default function ReportsPage() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [format, setFormat] = useState('json');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const formatOptions = [
    { value: 'json', label: 'JSON', icon: FileJson, description: 'Structured data for analysis' },
    { value: 'csv', label: 'CSV', icon: Table, description: 'Spreadsheet compatible' },
    { value: 'pdf', label: 'PDF', icon: FileCode, description: 'Formatted report (coming soon)' }
  ];

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await generatePilotReport(startDate, endDate, format);

      if (format === 'csv') {
        // Create and download CSV file
        const blob = new Blob([data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pilot-metrics-${startDate}-${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setResult({ success: true, message: 'CSV downloaded successfully' });
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error('Report generation failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadJSON = () => {
    if (!result?.data) return;
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pilot-metrics-${startDate}-${endDate}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/founder')}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-400" />
                Generate Reports
              </h1>
              <p className="text-slate-400 text-sm">Export pilot metrics for any date range</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Date Range Selection */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Date Range
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Format Selection */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Export Format</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {formatOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  format === opt.value
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                }`}
              >
                <opt.icon className={`w-6 h-6 mb-2 ${format === opt.value ? 'text-blue-400' : 'text-slate-400'}`} />
                <div className="font-medium text-white">{opt.label}</div>
                <div className="text-xs text-slate-400 mt-1">{opt.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full py-4 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Report...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Generate Report
            </>
          )}
        </button>

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {result?.success && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300">{result.message}</p>
          </div>
        )}

        {/* JSON Result Preview */}
        {result?.data && format === 'json' && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
              <h3 className="font-medium text-white">Report Data</h3>
              <button
                onClick={downloadJSON}
                className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download JSON
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-auto">
              <pre className="text-xs text-slate-300 whitespace-pre-wrap">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Report Contents Description */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Report Contents</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5"></div>
              <div>
                <div className="text-white font-medium">KPI Snapshots</div>
                <div className="text-slate-400">Daily metrics including WAU, DAU, retention saves, revenue protected</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5"></div>
              <div>
                <div className="text-white font-medium">Client Statistics</div>
                <div className="text-slate-400">Total clients, at-risk count, saved count, churned count</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-amber-400 mt-1.5"></div>
              <div>
                <div className="text-white font-medium">Communication Metrics</div>
                <div className="text-slate-400">SMS sent/received, emails, calls for the date range</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-violet-400 mt-1.5"></div>
              <div>
                <div className="text-white font-medium">Agent Activity</div>
                <div className="text-slate-400">Per-agent breakdown of sessions, tasks, and messages</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
